import { z } from 'zod';
export const CHAT_EVIDENCE_SERVICE_USERNAME = 'trecs-chat-evidence';
export const CHAT_EVIDENCE_AUDIENCE = 'trecs-chat-evidence';
export const CHAT_EVIDENCE_MAX_RESPONSE_BYTES = 65_536;
export const CHAT_EVIDENCE_MAX_REQUEST_BYTES = 32_768;
const id = z.string().min(1).max(256);
const revision = z.string().regex(/^[a-f0-9]{64}$/);
export const chatEvidenceFamilySchema = z.enum([
    'workscope',
    'arc',
    'ad',
    'sb',
    'llp',
    'engine_event',
    'thrust_conversion',
    'project_summary',
]);
export const chatEvidenceProjectFamilySchema = z.enum([
    'ad',
    'sb',
    'llp',
    'engine_event',
    'thrust_conversion',
    'project_summary',
]);
/** Families read per reconstructed document (Delivery A). */
export const chatEvidenceDocumentFamilySchema = z.enum(['workscope', 'arc']);
export const chatEvidenceScopeSchema = z
    .strictObject({
    documents: z
        .array(z.strictObject({ brainDocumentId: id, allowedPageIds: z.array(id).min(1).max(100).optional() }))
        .min(1)
        .max(50),
})
    .refine((scope) => new Set(scope.documents.map((d) => d.brainDocumentId)).size === scope.documents.length &&
    scope.documents.reduce((n, d) => n + (d.allowedPageIds?.length ?? 0), 0) <= 100);
const base = {
    version: z.literal(1),
    requestId: z.uuid(),
    ownerOrganisationId: id,
    expectedRevision: revision.optional(),
    page: z.strictObject({
        limit: z.number().int().min(1).max(50).default(20),
        cursor: z.string().min(1).max(2048).optional(),
    }),
};
export const chatEvidenceRequestSchema = z.discriminatedUnion('operation', [
    z.strictObject({
        ...base,
        scope: chatEvidenceScopeSchema,
        operation: z.literal('list_document_evidence'),
        query: z.strictObject({ family: chatEvidenceDocumentFamilySchema.optional() }),
    }),
    z.strictObject({
        ...base,
        scope: chatEvidenceScopeSchema,
        operation: z.literal('read_document_evidence'),
        query: z.strictObject({ family: chatEvidenceDocumentFamilySchema, reconstructedDocumentId: id }),
    }),
    z.strictObject({
        ...base,
        operation: z.literal('read_project_family'),
        scope: z.strictObject({ project: z.literal(true) }),
        query: z.strictObject({
            family: chatEvidenceProjectFamilySchema,
            status: z.array(z.string().min(1).max(40)).max(10).optional(),
            query: z.string().min(1).max(120).optional(),
        }),
    }),
]);
const text = z.string().max(1600).nullable().optional();
const number = z.number().finite().nullable().optional();
const texts = z.array(z.string().max(200)).max(30).nullable().optional();
export const chatWorkscopeValuesSchema = z.object({
    shopVisitRef: text,
    workOrders: texts,
    workscopeLevel: text,
    workscopeLevelRaw: text,
    modulesWorked: texts,
    // Per-module printed level (DR-5967); each `module` is a `modulesWorked` entry.
    moduleLevels: z
        .array(z.object({ module: z.string().max(200), levelRaw: z.string().max(200) }))
        .max(30)
        .nullable()
        .optional(),
    date: text,
    dateRaw: text,
    dateLabel: text,
    facility: text,
    tsn: number,
    csn: number,
    tslsv: number,
    cslsv: number,
    contestedFields: texts,
});
export const chatArcValuesSchema = z.object({
    partNumber: text,
    serialNumber: text,
    quantity: number,
    description: text,
    status: text,
    statusRaw: text,
    date: text,
    authority: text,
    formType: text,
    formTrackingNumber: text,
    workOrder: text,
    tsn: number,
    csn: number,
    isSigned: z.boolean().nullable().optional(),
});
const remarks = z.string().max(600).nullable().optional();
const statusSheet = { asOfDate: text, asOfDateSource: text };
export const chatAdValuesSchema = z.strictObject({
    adNumber: text,
    requirement: text,
    subject: text,
    status: text,
    statusRaw: text,
    remarksStatus: text,
    remarks,
    complianceDate: text,
    complianceMethod: text,
    nextDue: text,
    ...statusSheet,
});
export const chatSbValuesSchema = z.strictObject({
    sbNumber: text,
    revision: text,
    requirement: text,
    subject: text,
    status: text,
    statusRaw: text,
    remarksStatus: text,
    remarks,
    ...statusSheet,
    supersededRevisionCount: number,
});
export const chatLlpValuesSchema = z.strictObject({
    partNumber: text,
    serialNumber: text,
    name: text,
    csn: number,
    lifeLimit: number,
    remaining: number,
    chain: z
        .array(z.strictObject({ type: text, date: text, csn: number }))
        .max(30)
        .optional(),
});
export const chatEngineEventValuesSchema = z.strictObject({
    type: text,
    date: text,
    operator: text,
    aircraftReg: text,
    aircraftType: text,
    position: text,
    reason: text,
    tsn: number,
    csn: number,
    thrust: text,
});
export const chatThrustConversionValuesSchema = z.strictObject({
    fromThrustRating: text,
    toThrustRating: text,
    conversionDate: text,
    authorizingDoc: text,
});
export const chatProjectSummaryValuesSchema = z.strictObject({
    esn: text,
    engineType: text,
    manufactureDate: text,
    tsn: number,
    csn: number,
    tslsv: number,
    cslsv: number,
    asOfDate: text,
    isFinalised: z.boolean().optional(),
    familyCounts: z.record(z.string().max(40), z.number().int().min(0)).optional(),
});
const evidence = {
    kind: z.literal('evidence'),
    reconstructedDocumentId: id,
    rowKey: id,
    citationRefs: z.array(id).min(1).max(100),
};
// Project-family rows come from the finalised aggregate, not from one reconstructed document.
const projectEvidence = {
    kind: z.literal('evidence'),
    reconstructedDocumentId: id.nullable(),
    rowKey: id,
    citationRefs: z.array(id).min(1).max(100),
};
export const chatEvidenceRecordSchema = z.union([
    z.strictObject({
        kind: z.literal('document'),
        reconstructedDocumentId: id,
        family: chatEvidenceDocumentFamilySchema,
        sourceDocumentIds: z.array(id).min(1).max(50),
        extractionStatus: z.enum(['done', 'not_ready', 'unsupported']),
    }),
    z.strictObject({ ...evidence, family: z.literal('workscope'), values: chatWorkscopeValuesSchema }),
    z.strictObject({ ...evidence, family: z.literal('arc'), values: chatArcValuesSchema }),
    z.strictObject({ ...projectEvidence, family: z.literal('ad'), values: chatAdValuesSchema }),
    z.strictObject({ ...projectEvidence, family: z.literal('sb'), values: chatSbValuesSchema }),
    z.strictObject({ ...projectEvidence, family: z.literal('llp'), values: chatLlpValuesSchema }),
    z.strictObject({ ...projectEvidence, family: z.literal('engine_event'), values: chatEngineEventValuesSchema }),
    z.strictObject({
        ...projectEvidence,
        family: z.literal('thrust_conversion'),
        values: chatThrustConversionValuesSchema,
    }),
    z.strictObject({
        ...projectEvidence,
        family: z.literal('project_summary'),
        citationRefs: z.array(id).max(100),
        values: chatProjectSummaryValuesSchema,
    }),
]);
export const chatEvidenceDependencySchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('document'), id }),
    z.strictObject({ kind: z.literal('reconstruction'), id }),
    z.strictObject({ kind: z.literal('page'), id }),
]);
export const chatEvidenceResponseSchema = z
    .strictObject({
    version: z.literal(1),
    requestId: z.uuid(),
    projectId: id,
    ownerOrganisationId: id,
    operation: z.enum(['list_document_evidence', 'read_document_evidence', 'read_project_family']),
    result: z.enum(['data', 'unchanged', 'source_changed']),
    coverage: z.strictObject({
        status: z.enum(['ready', 'partial', 'not_ready', 'unsupported', 'no_matches']),
        scope: z.enum(['selected_documents', 'project']),
        hasMore: z.boolean(),
        reasons: z
            .array(z.enum(['scope_incomplete', 'source_not_ready', 'unsupported_projection', 'resource_limit']))
            .max(4),
    }),
    snapshot: z.strictObject({ revision, generatedAt: z.iso.datetime() }),
    records: z.array(chatEvidenceRecordSchema).max(50),
    sources: z
        .array(z.strictObject({
        citationRef: id,
        brainDocumentId: id,
        reconstructedDocumentId: id.nullable(),
        pageId: id,
        originalPageId: id,
        originalPageNumber: z.number().int().min(1),
        derivation: z.literal('extracted'),
    }))
        .max(100),
    dependencies: z.array(chatEvidenceDependencySchema).max(200),
    nextCursor: z.string().max(2048).optional(),
})
    .refine((response) => response.result === 'data' ||
    (response.records.length === 0 && response.sources.length === 0 && response.nextCursor === undefined), { message: 'Revision acknowledgements cannot contain evidence or pagination' });
//# sourceMappingURL=api-projects-id-chat-evidence-query.api.schema.js.map